import { env } from '../config/env';
import type { ClientLogLevel } from './log-level';

export const LOG_SCHEMA_VERSION = '1';

export interface ShippableLogEntry {
  level: ClientLogLevel;
  event: string;
  message: string;
  fields?: Record<string, unknown>;
  ts: string;
}

const SHIP_LEVELS: ClientLogLevel[] = ['fatal', 'error', 'warn'];
const MAX_BUFFER = 25;
const FLUSH_MS = 2_000;

const buffer: ShippableLogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let correlationIdProvider: () => string = () =>
  `fe-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export function setLogCorrelationIdProvider(fn: () => string): void {
  correlationIdProvider = fn;
}

export function shouldShipLevel(level: ClientLogLevel): boolean {
  return env.enableErrorTracking && SHIP_LEVELS.includes(level);
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushLogBuffer();
  }, FLUSH_MS);
}

function shipPayload(entries: ShippableLogEntry[]): void {
  const correlationId = correlationIdProvider();
  const body = JSON.stringify(entries);
  const url = `${env.apiUrl}/api/logs`;

  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    const ok = navigator.sendBeacon(url, blob);
    if (ok) return;
  }

  void fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Correlation-ID': correlationId,
    },
    body,
    keepalive: true,
  }).catch(() => {
    /* swallow — logging must not break the app */
  });
}

export function enqueueLogShip(entry: ShippableLogEntry): void {
  if (!shouldShipLevel(entry.level)) return;
  buffer.push(entry);
  if (buffer.length >= MAX_BUFFER) {
    flushLogBuffer();
    return;
  }
  scheduleFlush();
}

export function flushLogShip(): void {
  flushLogBuffer();
}

function flushLogBuffer(): void {
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, MAX_BUFFER);
  shipPayload(batch);
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => flushLogBuffer());
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushLogBuffer();
  });
}
